// src/hooks/useBlogPost.js - FIXED WITH BETTER ERROR HANDLING

import { useState, useEffect, useCallback } from 'react';
import { getBlog, getRelatedBlogs } from '../services/blogServices';
import { getBlogBanners } from '../services/advertsService';
import { trackBlogView } from "../services/analyticsService";
import { getVisitorId } from "../utils/visitorId";
import { toggleLikeBlog,  getLikeStatus } from '../services/blogServices';

export const useBlogPost = (slug) => {
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likesCount, setLikesCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [liking, setLiking] = useState(false);
  const [banners, setBanners] = useState({
    inline: [],
    sidebar: [],
    end_of_post: []
  });



  const fetchPost = useCallback(async () => {
    if (!slug) {
      setLoading(false);
      setError('No slug provided');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const [postResponse, relatedResponse] = await Promise.allSettled([getBlog(slug), getRelatedBlogs(slug),]);

      // Main post must succeed
      if (postResponse.status !== "fulfilled") {
        throw (
          postResponse.reason ||
          new Error("Failed to load blog post")
        );
      }

      const postData = postResponse.value.data;

      if (!postData) {
        throw new Error("No data received from API");
      }

      setPost(postData);
      setLikesCount(postData.likes_count || 0);

      // Related posts are optional
      if (relatedResponse.status === "fulfilled") {
        const relatedData = relatedResponse.value.data?.results || relatedResponse.value.data || [];

        setRelatedPosts(
          Array.isArray(relatedData)
            ? relatedData.slice(0, 5)
            : []
        );
      } else {
        setRelatedPosts([]);
      }

    } catch (err) {
      let errorMessage = "Unable to load blog post";

      if (err.response?.status === 404) {
        errorMessage = "404 - Post not found";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setPost(null);
    } finally{
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);


  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await getBlogBanners(slug);
        setBanners(res.data);
      } catch (error) {
          console.error("Failed to load banners", error);
        }
    };

    fetchBanners();
  }, [slug]);


  // Track post view count
  useEffect(() => {
    if (post) {
      trackBlogView(post.slug,getVisitorId());
    }
  }, [post]);

  useEffect(() => {
    const fetchLikeStatus = async () => {
      if (!post?.slug) return;

      try {
        const visitorId = getVisitorId();

        const res = await getLikeStatus(post.slug, visitorId);
        setLiked(res.data.liked);
      } catch (err) {
        console.error("Failed to fetch like status", err);
      }
    };

    if (post) {
      fetchLikeStatus();
    }
  }, [post]);

  // Like post
  const toggleLike = async () => {
    if (liking) return;

    setLiking(true);

    try {
      const visitorId = getVisitorId();

      const res = await toggleLikeBlog(slug, visitorId);
      console.log(localStorage.getItem('visitor_id'))

      // IMPORTANT: trust backend response, not assumption
      setLiked(res.data.liked);

      setLikesCount(prev =>
        res.data.liked ? prev + 1 : Math.max(prev - 1, 0)
      );

    } catch (error) {
      console.error("Like failed", error);
    } finally {
      setLiking(false);
    }
  };


  // Add structured data for SEO
  const getStructuredData = useCallback(() => {
    if (!post) return null;

    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.seo?.meta_title|| post.title,
      "description": post.seo?.meta_description || post.excerpt,
      "image": post.seo?.og_image || post.featured_image,
      "datePublished": post.published_at,
      "dateModified": post.updated_at || post.published_at,
      "author": {
        "@type": "Person",
        "name": post.author_name,
        "url": post.author_url
      },
      "publisher": {
        "@type": "Organization",
        "name": "DOROTS",
        "logo": {
          "@type": "ImageObject",
          "url": "https://dorots.com/logo.png"
        }
      }
    };
  }, [post]);

  return {
    post,
    relatedPosts,
    loading,
    error,
    
    likesCount,
    liked,
    toggleLike,

    banners,

    refresh: fetchPost,
    structuredData: getStructuredData()
  };
};